import { Timestamp } from 'firebase/firestore';

export enum OnboardingStatus {
  PENDING_INVITE = 'pending_invite',
  INVITE_SENT = 'invite_sent',
  PARTIALLY_COMPLETE = 'partially_complete',
  COMPLETE = 'complete'
}

export enum UserRole {
  // PropCloudAI System Level (1 role)
  PROPCLOUDAI_SUPER_USER = 'propcloudai_super_user',

  // PropInvest HQ Level (9 roles)
  HQ_MANAGER = 'hq_manager',
  HQ_ADMIN = 'hq_admin',
  HQ_BONDS = 'hq_bonds',
  HQ_CONSULTANT_LVL1 = 'hq_consultant_lvl1', // 30% commission
  HQ_INDEPENDENT_CONSULTANT_LVL2 = 'hq_independent_consultant_lvl2', // 50% commission
  HQ_REFERRAL_MANDATE = 'hq_referral_mandate', // 5% commission
  HQ_ADVERTISING = 'hq_advertising', // Marketing & advertising
  HQ_VIEWING_AGENT = 'hq_viewing_agent', // Property viewing management
  HQ_VIEWING_COORDINATOR = 'hq_viewing_coordinator', // Complete oversight of all viewings
  HQ_CALL_CENTRE_AGENT = 'hq_call_centre_agent', // Call centre agent (Euphoria dialer)

  // AI agents (server-side actors — no interactive login; the users doc exists
  // for identity, lead ownership, attribution and audit rows)
  AI_AGENT_LVL1 = 'ai_agent_lvl1', // ARIA — AI sales consultant, 0% commission

  // Aliases for marketing automation compatibility
  HQ_CONSULTANT = 'hq_consultant_lvl1',
  INDEPENDENT_CONSULTANT = 'hq_independent_consultant_lvl2',

  // ============================================================
  // DEPRECATED ROLES - Kept for backward compatibility during migration
  // These will be removed after all users are migrated
  // ============================================================
  /** @deprecated Use HQ_ADVERTISING instead */
  PROPCLOUDAI_ADVERTISING = 'propcloudai_advertising',
  /** @deprecated Use HQ_VIEWING_AGENT instead */
  VIEWING_AGENT = 'viewing_agent',
  /** @deprecated Role removed - users should be migrated to appropriate HQ role */
  PROPCLOUDAI_USER_MANAGEMENT = 'propcloudai_user_management',
  /** @deprecated Role removed - users should be migrated to appropriate HQ role */
  PROPCLOUDAI_CRM = 'propcloudai_crm',
  /** @deprecated Use HQ_INDEPENDENT_CONSULTANT_LVL2 instead - kept for reading existing data */
  INDEPENDENT_PARTNER_LVL3 = 'independent_partner_lvl3',
  /** @deprecated Use HQ_CONSULTANT_LVL1 instead - kept for reading existing data */
  PARTNER_AGENT_LVL3 = 'partner_agent_lvl3'
}

export enum TenantType {
  PROPCLOUDAI = 'propcloud_ai',  // Changed to avoid build verification false positive with Firebase project ID
  PROPINVEST_HQ = 'propinvest_hq',
  PARTNER_OFFICE = 'partner_office'
}

export interface User {
  id: string; // Document ID in Firestore
  uid: string; // Firebase Auth UID
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  photoURL: string;
  dateOfBirth?: Date;
  mobileNumber?: string;
  phone?: string; // Added for phone field
  role: UserRole;
  
  // Tenant fields
  tenantType: TenantType;
  tenantId: string; // 'system' | 'propinvest_hq' | partnerId
  tenantName: string; // Display name for the tenant

  // Multi-tenant partner ID (for SaaS model)
  partnerId: string; // Partner/organization this user belongs to (same as tenantId)

  // Commission fields
  commission?: number; // Deprecated - use commissionPercentage
  commissionType?: 'per_sale' | 'per_viewing'; // Type of commission for agents
  commissionPercentage?: number; // Base commission percentage
  /** Per-person OTP edit switch (Jaco 2026-10-22: Pieter Piek keeps it after moving to Level 1). */
  canEditOtp?: boolean;
  commissionBasePercentage?: number; // For agents: % of partner's commission
  
  employmentStartDate: Date | Timestamp;
  employmentContractURL?: string;
  onboardingStatus: OnboardingStatus;
  inviteSentAt?: Timestamp;
  onboardingCompletedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isActive: boolean;
  status?: 'active' | 'inactive' | 'pending'; // Added for status field
  
  // Google Calendar Integration
  googleCalendarIntegration?: {
    enabled: boolean;
    gmailEnabled?: boolean; // Gmail integration enabled
    linkedEmail: string;
    accessToken?: string; // Encrypted
    refreshToken?: string; // Encrypted
    tokenType?: string;
    expiryDate?: number;
    scope?: string;
    calendarId: string;
    linkedAt: Timestamp;
    // Token health. `enabled` only records that a link was once made — Google never
    // notifies us when a grant is revoked, so these are stamped server-side the first
    // time a call fails with invalid_grant, and cleared again on the next success.
    // Calendar failures land in lastSync*, Gmail send failures in gmailAuth*.
    lastSyncStatus?: 'ok' | 'auth_failed';
    lastSyncError?: string;
    lastSyncErrorAt?: Timestamp;
    lastSyncOkAt?: Timestamp;
    gmailAuthStatus?: 'auth_failed';
    gmailAuthError?: string;
    gmailAuthErrorAt?: Timestamp;
  };
  
  // Reminder Preferences
  reminderPreferences?: {
    inApp: {
      fifteenMin: boolean;
      oneHour: boolean;
    };
    email: {
      twentyFourHour: boolean;
      oneHour: boolean;
    };
  };

  // Custom permission overrides
  customPermissionId?: string; // Reference to UserCustomPermission document if exists
  hasCustomPermissions?: boolean; // Quick flag to check if user has custom permissions

  // Call Centre / Euphoria agent linking (for HQ_CALL_CENTRE_AGENT role)
  euphoriaAgentName?: string;      // Matches agentName in callDispositionLogs and Euphoria API responses
  euphoriaSipUsername?: string;     // SIP username for Euphoria real-time stats filtering
  euphoriaExtension?: string;      // Euphoria extension number

  // Calendar availability settings
  availabilitySettings?: {
    monday: { enabled: boolean; startTime: string; endTime: string; };
    tuesday: { enabled: boolean; startTime: string; endTime: string; };
    wednesday: { enabled: boolean; startTime: string; endTime: string; };
    thursday: { enabled: boolean; startTime: string; endTime: string; };
    friday: { enabled: boolean; startTime: string; endTime: string; };
    saturday: { enabled: boolean; startTime: string; endTime: string; };
    sunday: { enabled: boolean; startTime: string; endTime: string; };
    timezone?: string; // Default: 'Africa/Johannesburg'
  };

  // Vacation Mode (ADMIN-set only via setConsultantVacation CF — no consultant
  // self-service by decision, Jaco 2026-09-18). Absent = not on vacation.
  // Drives RR exclusion mirroring, Cindy booking/escalation cover routing and
  // call-centre visibility/blocking. Pure helpers: src/utils/vacation.ts.
  vacation?: {
    startDate: Timestamp;
    endDate: Timestamp;
    setBy?: string;
    setByName?: string;
    createdAt?: Timestamp;
    coverNoteForCc?: string;
    armedAt?: Timestamp;
  };
  vacationHistory?: Array<{
    startDate: Timestamp;
    endDate: Timestamp;
    setBy?: string | null;
    setByName?: string | null;
    endedAt: Timestamp;
    endedBy: string;
  }>;

  // Passkey/Biometric Authentication Preferences
  passkeySetupSkipped?: Timestamp; // When user skipped passkey setup
  passkeySetupPromptCount?: number; // How many times we've prompted for passkey
  biometricSetupSkipped?: Timestamp; // Legacy: When user skipped biometric setup
  biometricSetupPromptCount?: number; // Legacy: How many times we've prompted
  
  // Device management
  activeDevices?: {
    desktop?: {
      deviceId: string;
      deviceName: string;
      sessionId: string;
      lastSeen: Timestamp;
    };
    mobile?: {
      deviceId: string;
      deviceName: string;
      sessionId: string;
      lastSeen: Timestamp;
    };
  };
  
  // Hierarchy fields
  parentCompany?: string; // Deprecated - use tenantId
  parentUserId?: string; // For agents: their partner's user ID
  partnerName?: string; // Name of partner company (only for Partner role)
  managedAgents?: string[]; // Array of agent user IDs (only for Partner role)
  
  // Demo and testing fields
  isDemo?: boolean; // Flag for demo users
  category?: string; // Category for grouping users (e.g., "PropCloudAI System", "PropInvest HQ", "Partner")
  
  // Onboarding fields
  onboardingCompleted?: boolean;
  onboardingStep?: number;

  // CRM Preferences
  crmPreferences?: {
    navigationMode: 'tabs' | 'sidebar' | 'hidden';
    sidebarCollapsed: boolean;
    agendaDefaultView: 'today' | 'week' | 'all';
  };

  // Tour Preferences (for interactive onboarding tours)
  tourPreferences?: {
    welcomeModalShown: boolean;
    welcomeModalShownAt?: Timestamp;
    welcomeModalSkipped?: boolean;
    completedTours: string[];
    tourProgress: Record<string, { completedAt?: Timestamp; stepsCompleted: number }>;
    preferences: {
      autoStartTours: boolean;
      showTourHints: boolean;
    };
  };

  // Email Signature Preferences
  emailSignaturePreferences?: {
    enabled: boolean;
    useCustomSignature: boolean;
    customSignatureHtml: string;
    includeName: boolean;
    includeRole: boolean;
    includeEmail: boolean;
    includePhone: boolean;
    includeCompany: boolean;
  };

  // PropData Syndication Config (per-user branch override)
  syndicationConfig?: {
    branchId: number | null;        // Custom branch ID (null = use default)
    branchName: string | null;      // Cached for display
    useDefaultBranch: boolean;      // true = inherit system default, false = use branchId
  };
}

/**
 * Check if a user is active (checks both isActive and status fields)
 *
 * This function provides a consistent way to check user active status
 * across the entire codebase. It checks BOTH fields because:
 * - isActive: boolean field used by login check and Round Robin
 * - status: string field used by UserManagement UI components
 *
 * User is considered INACTIVE if EITHER:
 * - isActive is explicitly false
 * - status is 'inactive'
 *
 * @param user - User object (can be partial, null, or undefined)
 * @returns true if user is active, false if inactive or user is null/undefined
 *
 * @example
 * ```typescript
 * if (!isUserActive(userProfile)) {
 *   // Handle inactive user
 * }
 * ```
 */
export function isUserActive(user: Partial<User> | null | undefined): boolean {
  if (!user) return false;

  // User is INACTIVE if EITHER field indicates inactive
  const isExplicitlyInactive = user.isActive === false || user.status === 'inactive';
  return !isExplicitlyInactive;
}